import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import LeaseDealCard from "@/components/LeaseDealCard";
import { Slider } from "@/components/ui/slider";
import { Input } from "@/components/ui/input";
import { Search, Sparkles, SlidersHorizontal, X, BookmarkPlus, ShieldCheck, ArrowRight } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const BRANDS = ["BMW", "Mercedes-Benz", "Audi", "Porsche", "Tesla", "Lexus", "Acura", "Genesis", "Cadillac", "Toyota", "Honda", "Ford", "Chevrolet", "Volvo", "Jeep"];
const TYPES = [
  { v: "sedan", l: "Sedan" },
  { v: "suv", l: "SUV" },
  { v: "truck", l: "Truck" },
  { v: "coupe", l: "Coupe" },
  { v: "ev", l: "EV" },
];

function Filters({ state, setState, brands }) {
  const set = (k, v) => setState((s) => ({ ...s, [k]: v }));
  return (
    <div className="space-y-7" data-testid="lease-filters">
      <div>
        <div className="label-mono mb-3">Search</div>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
          <Input
            placeholder="Brand, model, trim…"
            value={state.search || ""}
            onChange={(e) => set("search", e.target.value)}
            className="bg-[#0A0A0E] border-[#1A1A24] text-white pl-9 h-11 rounded-full"
            data-testid="filter-search-input"
          />
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-3">
          <div className="label-mono">Monthly Payment</div>
          <div className="text-xs text-gray-400">${state.maxPayment}/mo</div>
        </div>
        <Slider
          min={200}
          max={2000}
          step={50}
          value={[state.maxPayment]}
          onValueChange={(v) => set("maxPayment", v[0])}
          data-testid="filter-max-payment"
        />
      </div>

      <div>
        <div className="flex items-center justify-between mb-3">
          <div className="label-mono">Max Due at Signing</div>
          <div className="text-xs text-gray-400">${state.maxDown}</div>
        </div>
        <Slider
          min={0}
          max={10000}
          step={500}
          value={[state.maxDown]}
          onValueChange={(v) => set("maxDown", v[0])}
          data-testid="filter-max-down"
        />
      </div>

      <div>
        <div className="label-mono mb-3">Brand</div>
        <div className="flex flex-wrap gap-1.5">
          <button
            onClick={() => set("brand", "")}
            data-testid="filter-brand-all"
            className={`text-xs px-3 py-1.5 rounded-full border ${!state.brand ? "silver-gradient text-[#050507] border-transparent" : "border-white/10 text-gray-400 hover:text-white"}`}
          >
            All
          </button>
          {brands.map((b) => (
            <button
              key={b}
              onClick={() => set("brand", b)}
              data-testid={`filter-brand-${b.toLowerCase().replace(/\s+/g, "-")}`}
              className={`text-xs px-3 py-1.5 rounded-full border ${state.brand === b ? "silver-gradient text-[#050507] border-transparent" : "border-white/10 text-gray-400 hover:text-white"}`}
            >
              {b}
            </button>
          ))}
        </div>
      </div>

      <div>
        <div className="label-mono mb-3">Type</div>
        <div className="flex flex-wrap gap-1.5">
          <button onClick={() => set("vehicleType", "")} className={`text-xs px-3 py-1.5 rounded-full border ${!state.vehicleType ? "silver-gradient text-[#050507] border-transparent" : "border-white/10 text-gray-400"}`} data-testid="filter-type-all">All</button>
          {TYPES.map((t) => (
            <button key={t.v} onClick={() => set("vehicleType", t.v)} data-testid={`filter-type-${t.v}`} className={`text-xs px-3 py-1.5 rounded-full border ${state.vehicleType === t.v ? "silver-gradient text-[#050507] border-transparent" : "border-white/10 text-gray-400"}`}>
              {t.l}
            </button>
          ))}
        </div>
      </div>

      <div className="flex gap-2">
        <button
          onClick={() => set("isLuxury", state.isLuxury ? null : true)}
          className={`text-xs px-3 py-1.5 rounded-full border ${state.isLuxury ? "silver-gradient text-[#050507] border-transparent" : "border-white/10 text-gray-400"}`}
          data-testid="filter-luxury"
        >
          Luxury
        </button>
        <button
          onClick={() => set("isEv", state.isEv ? null : true)}
          className={`text-xs px-3 py-1.5 rounded-full border ${state.isEv ? "silver-gradient text-[#050507] border-transparent" : "border-white/10 text-gray-400"}`}
          data-testid="filter-ev"
        >
          Electric Only
        </button>
      </div>
    </div>
  );
}

export default function LeaseDeals() {
  const { user } = useAuth();
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [aiQuery, setAiQuery] = useState("");
  const [aiAnswer, setAiAnswer] = useState("");
  const [aiBusy, setAiBusy] = useState(false);
  const [savingSearch, setSavingSearch] = useState(false);

  const [state, setState] = useState({
    brand: "",
    vehicleType: "",
    maxPayment: 2000,
    maxDown: 10000,
    isLuxury: null,
    isEv: null,
    search: "",
  });

  const [authorizedPreview, setAuthorizedPreview] = useState([]);
  useEffect(() => {
    api.get("/authorized-inventory", { params: { limit: 6 } })
      .then((r) => setAuthorizedPreview(r.data))
      .catch(() => setAuthorizedPreview([]));
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = {
      limit: 100,
      max_payment: state.maxPayment < 2000 ? state.maxPayment : undefined,
      max_down: state.maxDown < 10000 ? state.maxDown : undefined,
      brand: state.brand || undefined,
      vehicle_type: state.vehicleType || undefined,
      is_luxury: state.isLuxury || undefined,
      is_ev: state.isEv || undefined,
      search: state.search || undefined,
    };
    Object.keys(params).forEach((k) => params[k] === undefined && delete params[k]);
    api.get("/lease-deals", { params })
      .then((r) => setDeals(r.data))
      .finally(() => setLoading(false));
  }, [state]);

  const askAi = async () => {
    if (!aiQuery.trim()) return;
    setAiBusy(true);
    setAiAnswer("");
    try {
      const { data } = await api.post("/chat", { message: aiQuery });
      setAiAnswer(data.reply);
    } catch {
      setAiAnswer("Couldn't reach the AI right now. Try a manual filter.");
    } finally {
      setAiBusy(false);
    }
  };

  const saveSearch = async () => {
    if (!user) { toast.error("Sign in to save searches"); window.location.href = "/login"; return; }
    const name = window.prompt("Name this search:", `${state.brand || "Any brand"} · ${state.vehicleType || "any type"}`);
    if (!name) return;
    setSavingSearch(true);
    const filters = {};
    Object.entries(state).forEach(([k, v]) => { if (v !== "" && v !== null && v !== 2000 && v !== 10000) filters[k] = v; });
    try {
      await api.post("/saved-searches", { name, filters });
      toast.success("Search saved. You'll see it in your dashboard.");
    } catch { toast.error("Couldn't save."); }
    finally { setSavingSearch(false); }
  };

  return (
    <div className="max-w-[1500px] mx-auto px-5 lg:px-10 py-12 md:py-16" data-testid="lease-deals-page">
      <div className="mb-10">
        <p className="label-mono mb-3">Lease Deal Marketplace</p>
        <h1 className="font-display text-4xl md:text-6xl font-light tracking-tighter">Every brand. Every deal.</h1>
        <p className="text-gray-400 max-w-2xl mt-3">Curated lease offers refreshed regularly. Use filters or ask the 555 Lease Deal Finder in natural language.</p>
      </div>

      {/* AI Deal Finder */}
      <div className="glass rounded-2xl p-5 md:p-6 mb-10" data-testid="ai-deal-finder">
        <div className="flex items-center gap-2 mb-3">
          <Sparkles className="w-4 h-4 text-silver" />
          <div className="label-mono">555 Lease Deal Finder · AI</div>
        </div>
        <div className="flex gap-2 flex-col md:flex-row">
          <Input
            value={aiQuery}
            onChange={(e) => setAiQuery(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && askAi()}
            placeholder="Find me a BMW SUV under $700/mo with low down payment…"
            className="flex-1 bg-[#0A0A0E] border-[#1A1A24] text-white h-12 rounded-full px-5"
            data-testid="ai-finder-input"
          />
          <button
            onClick={askAi}
            disabled={aiBusy}
            className="px-6 py-3 rounded-full silver-gradient text-[#050507] font-bold disabled:opacity-50"
            data-testid="ai-finder-submit"
          >
            {aiBusy ? "Thinking…" : "Ask AI"}
          </button>
        </div>
        {aiAnswer && (
          <div className="mt-4 p-4 rounded-xl bg-white/5 text-sm leading-relaxed" data-testid="ai-finder-answer">
            {aiAnswer}
          </div>
        )}
      </div>

      {/* Authorized Inventory strip */}
      {authorizedPreview.length > 0 && (
        <div className="mb-10" data-testid="authorized-strip">
          <div className="flex items-end justify-between mb-4">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <ShieldCheck className="w-4 h-4 text-emerald-400" />
                <div className="label-mono">Authorized Dealer Inventory</div>
              </div>
              <h2 className="font-display text-2xl md:text-3xl tracking-tight">Prefer to buy? Real cars, real dealers.</h2>
            </div>
            <a href="/inventory" className="hidden md:inline-flex items-center gap-1 text-sm text-gray-400 hover:text-white" data-testid="view-all-inventory">
              View all inventory <ArrowRight className="w-4 h-4" />
            </a>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {authorizedPreview.slice(0, 6).map((v) => (
              <a key={v.id} href={`/inventory/${v.id}`} className="group block bg-[#0A0A0E] border border-[#1A1A24] rounded-xl overflow-hidden hover:border-white/20 transition-all" data-testid={`inv-strip-${v.id}`}>
                <div className="aspect-[4/3] bg-[#050507] overflow-hidden">
                  {v.image_urls?.[0] ? (
                    <img src={v.image_urls[0]} alt={`${v.year} ${v.make} ${v.model}`} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-[10px] text-gray-600">No photo</div>
                  )}
                </div>
                <div className="p-2.5">
                  <div className="text-xs font-medium leading-tight truncate">{v.year} {v.make} {v.model}</div>
                  <div className="text-[11px] text-gray-500 mt-0.5 font-mono">${v.price?.toLocaleString()}</div>
                </div>
              </a>
            ))}
          </div>
          <a href="/inventory" className="md:hidden mt-3 text-sm text-gray-400 inline-flex items-center gap-1">View all inventory <ArrowRight className="w-4 h-4" /></a>
        </div>
      )}

      <div className="grid lg:grid-cols-12 gap-8">
        {/* desktop filters */}
        <aside className="hidden lg:block lg:col-span-3">
          <div className="sticky top-24 bg-[#0A0A0E] border border-[#1A1A24] rounded-2xl p-6">
            <div className="flex items-center justify-between mb-5">
              <div className="font-display text-lg">Filters</div>
              <SlidersHorizontal className="w-4 h-4 text-gray-500" />
            </div>
            <Filters state={state} setState={setState} brands={BRANDS} />
          </div>
        </aside>

        {/* mobile filters */}
        <div className="lg:hidden mb-4">
          <Sheet>
            <SheetTrigger asChild>
              <button className="flex items-center gap-2 px-4 py-2.5 rounded-full border border-white/10 text-sm" data-testid="mobile-filter-trigger">
                <SlidersHorizontal className="w-4 h-4" /> Filters
              </button>
            </SheetTrigger>
            <SheetContent side="left" className="bg-[#0A0A0E] border-white/10 text-white overflow-y-auto">
              <SheetHeader><SheetTitle className="text-white font-display">Filters</SheetTitle></SheetHeader>
              <div className="mt-6"><Filters state={state} setState={setState} brands={BRANDS} /></div>
            </SheetContent>
          </Sheet>
        </div>

        <div className="lg:col-span-9">
          <div className="flex items-center justify-between mb-5">
            <p className="text-sm text-gray-400" data-testid="deals-count">{loading ? "Loading…" : `${deals.length} deals`}</p>
            <button onClick={saveSearch} disabled={savingSearch} className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-white/15 text-xs hover:bg-white/5 disabled:opacity-50" data-testid="save-search-btn">
              <BookmarkPlus className="w-3.5 h-3.5" /> Save Search
            </button>
          </div>
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="aspect-[5/6] bg-[#0A0A0E] border border-[#1A1A24] rounded-2xl animate-pulse" />
              ))}
            </div>
          ) : deals.length === 0 ? (
            <div className="text-center py-20 border border-dashed border-white/10 rounded-2xl px-6" data-testid="deals-empty-state">
              <div className="font-display text-2xl mb-3">No verified lease deal currently available{state.brand ? ` for ${state.brand}` : ""}.</div>
              <p className="text-gray-400 max-w-lg mx-auto mb-7">We're currently verifying lease offers from official manufacturer, dealer, and approved partner sources. In the meantime, our broker team can source it for you.</p>
              <div className="flex flex-wrap justify-center gap-3">
                <a href="/find-my-car" className="px-6 py-3 rounded-full silver-gradient text-[#050507] font-bold text-sm" data-testid="empty-request-vehicle">Request This Vehicle</a>
                <a href="/booking" className="px-6 py-3 rounded-full border border-white/15 text-sm hover:bg-white/5">Talk to a Broker</a>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5" data-testid="deals-grid">
              {deals.map((d) => <LeaseDealCard key={d.id} deal={d} />)}
            </div>
          )}
        </div>
      </div>

      <p className="text-[11px] text-gray-500 mt-12 max-w-3xl">
        Lease offers are subject to credit approval, location, tax, title, registration, dealer fees, and availability. Final payment may vary.
      </p>
    </div>
  );
}
